import { rem } from 'polished'
import type { PropsWithChildren } from 'react'
import styled from 'styled-components'

import { SVG_ICON_SIZE_CSS_VAR, StyledSvgIcon } from 'LuxKit/SvgIcon'
import { setCSSVar } from 'lib/theme/themeUtils'

export const TEARDROP_PIN_WIDTH = 22
export const TEARDROP_PIN_HEIGHT = 25

export enum GoogleMarkerTypes {
  food = 'food',
  retail = 'retail',
  entertainment = 'entertainment',
  transportation = 'transportation',
  outdoor = 'outdoor',
  emergency = 'emergency',
}

export type GoogleMarkerType = keyof typeof GoogleMarkerTypes

const ColouredPath = styled.path`
  &.${GoogleMarkerTypes.food} {
    fill: #ff9e67;
  }
  &.${GoogleMarkerTypes.retail} {
    fill: #4b96f3;
  }
  &.${GoogleMarkerTypes.entertainment} {
    fill: #13b5c7;
  }
  &.${GoogleMarkerTypes.transportation} {
    fill: #10bdff;
  }
  &.${GoogleMarkerTypes.outdoor} {
    fill: #1ccb77;
  }
  &.${GoogleMarkerTypes.emergency} {
    fill: #f88181;
  }
`

const StyledSvg = styled.svg`
  pointer-events: none;
  color: ${(props) => props.theme.palette.brand.primary.normal};
  overflow: visible;

  & > path {
    pointer-events: all;
    cursor: pointer;
  }
`

const IconContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: start;
  padding-top: ${rem(4)};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  > ${StyledSvgIcon} {
    ${setCSSVar(SVG_ICON_SIZE_CSS_VAR, '54.54%')}
  }
`

const Container = styled.div`
  position: relative;
  width: auto;
  height: auto;
`

interface Props {
  type: GoogleMarkerType
  size?: 'small' | 'large'
}

const scale = {
  small: 1,
  large: 30 / TEARDROP_PIN_HEIGHT,
}

function GooglePlacePin({
  type,
  children,
  size = 'small',
}: PropsWithChildren<Props>) {
  return (
    <Container>
      <StyledSvg
        width={TEARDROP_PIN_WIDTH * scale[size]}
        height={TEARDROP_PIN_WIDTH * scale[size]}
        viewBox="0 0 22 25"
      >
        <mask
          id="path-1-outside-1_3808_34925"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0.467285"
          width="22"
          height="29"
          fill="black"
        >
          <rect fill="white" y="0.467285" width="22" height="29" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.5973 18.9825C19.6833 17.1498 21 14.4623 21 11.4673C21 5.94444 16.5228 1.46729 11 1.46729C5.47715 1.46729 1 5.94444 1 11.4673C1 14.4862 2.33778 17.1927 4.45286 19.0263C8.34273 22.8139 10.0574 26.583 10.7221 28.4384C10.8188 28.7086 11.3432 28.705 11.4362 28.4335C12.0748 26.5699 13.7392 22.7841 17.5934 18.9864C17.5947 18.9851 17.596 18.9838 17.5973 18.9825Z"
          />
        </mask>
        <ColouredPath
          className={type}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.5973 18.9825C19.6833 17.1498 21 14.4623 21 11.4673C21 5.94444 16.5228 1.46729 11 1.46729C5.47715 1.46729 1 5.94444 1 11.4673C1 14.4862 2.33778 17.1927 4.45286 19.0263C8.34273 22.8139 10.0574 26.583 10.7221 28.4384C10.8188 28.7086 11.3432 28.705 11.4362 28.4335C12.0748 26.5699 13.7392 22.7841 17.5934 18.9864C17.5947 18.9851 17.596 18.9838 17.5973 18.9825Z"
        />
        <ColouredPath
          className={type}
          d="M17.5973 18.9825L17.0693 18.3815L17.0438 18.4038L17.0204 18.4282L17.5973 18.9825ZM4.45286 19.0263L5.01096 18.4531L4.99438 18.4369L4.97689 18.4218L4.45286 19.0263ZM10.7221 28.4384L11.4752 28.1686L10.7221 28.4384ZM11.4362 28.4335L12.193 28.6929H12.193L11.4362 28.4335ZM17.5934 18.9864L18.1549 19.5563L18.1549 19.5562L17.5934 18.9864ZM20.2 11.4673C20.2 14.2226 18.9898 16.6942 17.0693 18.3815L18.1253 19.5835C20.3769 17.6054 21.8 14.702 21.8 11.4673H20.2ZM11 2.26729C16.081 2.26729 20.2 6.38627 20.2 11.4673H21.8C21.8 5.50261 16.9647 0.667285 11 0.667285V2.26729ZM1.8 11.4673C1.8 6.38627 5.91898 2.26729 11 2.26729V0.667285C5.03532 0.667285 0.2 5.50261 0.2 11.4673H1.8ZM4.97689 18.4218C3.02962 16.7337 1.8 14.2445 1.8 11.4673H0.2C0.2 14.7279 1.64593 17.6517 3.92884 19.6307L4.97689 18.4218ZM11.4752 28.1686C10.7799 26.2277 9.00786 22.3449 5.01096 18.4531L3.89476 19.5994C7.6776 23.2828 9.33486 26.9383 9.96892 28.7082L11.4752 28.1686ZM10.6794 28.1742C10.7351 28.0118 10.8469 27.9216 10.92 27.8822C10.9885 27.8454 11.0433 27.8393 11.0751 27.8391C11.1069 27.8389 11.1617 27.8442 11.2306 27.8801C11.3043 27.9184 11.4173 28.007 11.4752 28.1686L9.96892 28.7082C10.1839 29.3084 10.7769 29.4412 11.086 29.4391C11.3952 29.4369 11.9863 29.296 12.193 28.6929L10.6794 28.1742ZM17.0319 18.4166C13.0692 22.3212 11.3478 26.2236 10.6794 28.1742L12.193 28.6929C12.8018 26.9161 14.4093 23.247 18.1549 19.5563L17.0319 18.4166ZM17.0204 18.4282C17.0246 18.4238 17.0285 18.42 17.0319 18.4166L18.1549 19.5562C18.1609 19.5503 18.1674 19.5438 18.1741 19.5368L17.0204 18.4282Z"
          mask="url(#path-1-outside-1_3808_34925)"
        />
        <path
          d="M17.5973 18.9825L17.0693 18.3815L17.0438 18.4038L17.0204 18.4282L17.5973 18.9825ZM4.45286 19.0263L5.01096 18.4531L4.99438 18.4369L4.97689 18.4218L4.45286 19.0263ZM10.7221 28.4384L11.4752 28.1686L10.7221 28.4384ZM11.4362 28.4335L12.193 28.6929H12.193L11.4362 28.4335ZM17.5934 18.9864L18.1549 19.5563L18.1549 19.5562L17.5934 18.9864ZM20.2 11.4673C20.2 14.2226 18.9898 16.6942 17.0693 18.3815L18.1253 19.5835C20.3769 17.6054 21.8 14.702 21.8 11.4673H20.2ZM11 2.26729C16.081 2.26729 20.2 6.38627 20.2 11.4673H21.8C21.8 5.50261 16.9647 0.667285 11 0.667285V2.26729ZM1.8 11.4673C1.8 6.38627 5.91898 2.26729 11 2.26729V0.667285C5.03532 0.667285 0.2 5.50261 0.2 11.4673H1.8ZM4.97689 18.4218C3.02962 16.7337 1.8 14.2445 1.8 11.4673H0.2C0.2 14.7279 1.64593 17.6517 3.92884 19.6307L4.97689 18.4218ZM11.4752 28.1686C10.7799 26.2277 9.00786 22.3449 5.01096 18.4531L3.89476 19.5994C7.6776 23.2828 9.33486 26.9383 9.96892 28.7082L11.4752 28.1686ZM10.6794 28.1742C10.7351 28.0118 10.8469 27.9216 10.92 27.8822C10.9885 27.8454 11.0433 27.8393 11.0751 27.8391C11.1069 27.8389 11.1617 27.8442 11.2306 27.8801C11.3043 27.9184 11.4173 28.007 11.4752 28.1686L9.96892 28.7082C10.1839 29.3084 10.7769 29.4412 11.086 29.4391C11.3952 29.4369 11.9863 29.296 12.193 28.6929L10.6794 28.1742ZM17.0319 18.4166C13.0692 22.3212 11.3478 26.2236 10.6794 28.1742L12.193 28.6929C12.8018 26.9161 14.4093 23.247 18.1549 19.5563L17.0319 18.4166ZM17.0204 18.4282C17.0246 18.4238 17.0285 18.42 17.0319 18.4166L18.1549 19.5562C18.1609 19.5503 18.1674 19.5438 18.1741 19.5368L17.0204 18.4282Z"
          fill="black"
          fillOpacity="0.2"
          mask="url(#path-1-outside-1_3808_34925)"
        />
      </StyledSvg>
      <IconContainer>{children}</IconContainer>
    </Container>
  )
}

export default GooglePlacePin
